import copy from 'fast-copy';

import { Injectable } from '@angular/core';
import { Observable, of, throwError } from 'rxjs';
import { catchError, map, share, take, tap } from 'rxjs/operators';

import { KlantinstellingenbeheerResource } from '@ap/interface-apic-klantinstellingenbeheer';

import { MijnKlantinstellingenMapper } from './mijn-klantinstellingen.mapper';
import { CustomerSettingCategory, CustomerSettingName, CustomerSettingValue } from './mijn-klantinstellingen.model';

/**
 * @ngdoc service
 * @name MijnKlantinstellingenService
 */
@Injectable({ providedIn: 'root' })
export class MijnKlantinstellingenService {
    private readonly cacheMap = new Map<string, CustomerSettingValue>();

    public constructor(private readonly klantinstellingenbeheerResource: KlantinstellingenbeheerResource) {}

    private static parseValue(value: string, isJson: boolean): CustomerSettingValue {
        if (isJson) {
            return JSON.parse(value);
        }
        if (value === 'true') {
            return true;
        }
        if (value === 'false') {
            return false;
        }
        if (!Number.isNaN(+value)) {
            return +value;
        }

        return value;
    }

    private static stringifyValue(value: CustomerSettingValue): string {
        if (value === true) {
            return 'true';
        }
        if (value === false) {
            return 'false';
        }
        if (typeof value === 'string') {
            return value;
        }
        if (typeof value === 'number') {
            return value.toString();
        }

        return JSON.stringify(value);
    }

    public getCustomerSetting<T extends CustomerSettingName>(
        category: CustomerSettingCategory,
        name: T,
        useCache = true,
        notFoundValue?: CustomerSettingValue<T>
    ): Observable<CustomerSettingValue<T>> {
        const cacheKey = this.getCacheKey(category, name);

        if (useCache && this.cacheMap.has(cacheKey)) {
            return of(copy(this.cacheMap.get(cacheKey)) as CustomerSettingValue<T>);
        }

        return this.getCustomerSettingValue(category, name).pipe(
            take(1),
            map((result) => copy(result) ?? notFoundValue),
            catchError((err) => (notFoundValue != null ? of(notFoundValue) : throwError(() => err)))
        ) as Observable<CustomerSettingValue<T>>;
    }

    public setCustomerSetting<T extends CustomerSettingName>(
        category: CustomerSettingCategory,
        name: T,
        value: CustomerSettingValue<T>,
        cache = true
    ): Observable<CustomerSettingValue<T>> {
        return this.klantinstellingenbeheerResource
            .createOrUpdateKlantinstelling({
                categorie: category,
                klantinstelling_type: {
                    naam: name
                },
                waarde: MijnKlantinstellingenService.stringifyValue(value)
            })
            .pipe(
                map(() => value),
                tap(() => cache && this.cacheMap.set(this.getCacheKey(category, name), value))
            );
    }

    private getCacheKey(category: CustomerSettingCategory, name: CustomerSettingName): string {
        return `${category}_${name}`;
    }

    private getCustomerSettingValue(
        category: CustomerSettingCategory,
        name: CustomerSettingName,
        cache = true
    ): Observable<CustomerSettingValue> {
        const cacheKey = this.getCacheKey(category, name);

        return this.klantinstellingenbeheerResource.getKlantinstellingen(category, name).pipe(
            map((result) => result?.klantinstellingen?.[0]),
            map(
                (result) => result && MijnKlantinstellingenService.parseValue(result.waarde, result.klantinstelling_type.is_json as boolean)
            ),
            map((result) => MijnKlantinstellingenMapper.map(name, result as any)),
            tap((result) => result !== undefined && cache && this.cacheMap.set(cacheKey, result)),
            share()
        );
    }
}
